import { Controller } from "stimulus"
import useColorScheme from "use-color-scheme"

const formatNumber = new Intl.NumberFormat(undefined, {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2 }).format

const formatPercentage = (value, suffix = "%") => {
  const formatted = formatNumber(value) + suffix
  return formatted.trim()
}

const initialChartOptions = {
  cutout: "75%",
  responsive: true,
  maintainAspectRatio: false,
  legend: false,
  tooltips: { enabled: false },
  hover: { mode: null },
  animation: { animateScale: false, animateRotate: true }
}

export default class extends Controller {
  static targets = [ "chart", "count", "diff", "previousResult" ]
  static values = {
    type: { type: String, default: "bench" },
    denominator: { type: Object },
    unit: String,
    labels: Array,
    palette: { type: Array, default: [ '#b5d294', '#94b5d2' ] }
  }

  get config() {
    return {
      type: "doughnut",
      options: {
        ...initialChartOptions,
        plugins: {
          centerText: {
            display: true,
            text: this.centerTextLabel,
            color: this.paletteValue
          },
          tooltip: {
            filter(context) {
              return context.raw.tooltip !== false
            },
            callbacks: {
              label(context) {
                return ` ${context.dataset.label}: ${formatPercentage(context.parsed)}`
              }
            }
          }
        }
      }
    }
  }

  get datasets() {
    return this.getTypeValue("current").map((value, i) => {
      return {
        label: this.labelsValue[i],
        data: [
          { value: this.denominatorValue[this.typeValue][i] - value, tooltip: false },
          { value: value }
        ],
        ...this.#datasetsConfig(i)
      }
    })
  }

  #datasetsConfig(i) {
    return {
      backgroundColor: [ this.cssBorderColor, this.paletteValue[i] ],
      borderColor: this.cssBackgroundColor
    }
  }

  get cssBorderColor() {
    return getComputedStyle(this.element).getPropertyValue('--bulma-border').trim()
  }

  get cssBackgroundColor() {
    return getComputedStyle(this.element).getPropertyValue('--bulma-background').trim()
  }

  get centerTextLabel() {
    return this.getTypeValue("current").map(v => formatPercentage(v, this.unitValue))
  }

  connect() {
    useColorScheme(this)

    const max = this.getTypeValue("current").length
    for (let i = 1; i < max; i++) {
      this.diffTarget.parentNode.parentNode.insertBefore(
        this.diffTarget.parentNode.cloneNode(true),
        this.diffTarget.parentNode
      )
    }

    this.chart = new window.Chart(this.chartTarget.getContext("2d"), this.config)
    this.updateChart()
  }

  connect() {
    const max = this.getTypeValue("current").length
    for (let i = 1; i < max; i++) {
      this.diffTarget.parentNode.parentNode.insertBefore(
        this.diffTarget.parentNode.cloneNode(true),
        this.diffTarget.parentNode
      )
    }

    this.updateChart()
  }

  disconnect() {
    this.chart?.destroy()
  }

  colorSchemeChanged() {
    this.chart.data.datasets.forEach((ds, i) => {
      ds.backgroundColor = this.#datasetsConfig(i).backgroundColor
      ds.borderColor = this.#datasetsConfig(i).borderColor
    })
    Object.assign(this.chart.config.options, this.options)
    this.chart.update('none')
  }

  typeValueChanged() {
    this.updateChart()
  }

  updateChart() {
    if (!this.chart) { return }

    this.chart.data.datasets = this.datasets
    this.chart.config.options.plugins.centerText.text = this.centerTextLabel
    this.chart.update()

    this.#updateAuxiliaryUI()
  }

  #updateAuxiliaryUI() {
    this.countTarget.innerHTML = JSON.parse(this.data.get("count"))
      .map((c, i) => `<b style="color:${this.paletteValue[i]};">${c}</b>`)
      .join(" / ")

    const diffValues = this.getTypeValue("diff")
    const prevValues = this.getTypeValue("previous")
    this.getTypeValue("current").forEach((v, i) => {
      const diffTarget = this.diffTargets[i]
      if (diffTarget) {
        const diff = diffValues[i]
        diffTarget.innerHTML = diff + this.unitValue
        diffTarget.className = "site-chart-diff-" + { "-": "down", "+": "up" }[diff[0]]
        diffTarget.parentNode.style.setProperty('--dot-color', this.paletteValue[i])
      }

      const el = this.previousResultTargets[i]
      if (el) {
        el.innerHTML = formatPercentage(prevValues[i], this.unitValue)
      }
    })
  }

  getTypeValue(key) {
    return JSON.parse(this.data.get(key))[this.typeValue]
  }
}
